import {
  Box, Card, CardContent, Typography, Grid, TableBody, TableRow, TableCell, Table,
  TableHead, TableContainer, Paper, LinearProgress, Button, Tooltip, IconButton,
} from '@mui/material'
import { ClassNameMap, withStyles } from '@mui/styles'
import React, { useEffect, useState } from 'react'
import BlockIcon from '@mui/icons-material/Block';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import stylesheet from '../detail-container.stylesheet'
import { PatientService } from '../../../../services/patient.service'
import Helper from '../../../../utils/helper'
import { useCurrentUser } from '../../../../utils/use-current-user'
import CardLayoutComponent from '../common/card-layout.component'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux/store'
import { JsonView, defaultStyles } from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';
import MedicationsComponent from '../common/medications.component'
import { toast } from 'react-toastify'
import { checkCondition } from '../common/condition-check'
import IngredientTooltip from '../common/ingredient-tooltip'
import AddNewMedicationCard from '../medications/add-medications.component'
import MedDetailsComponent from '../common/med-details.component'
import NotesComponent from '../common/notes.component'
import { useUpdateDataAction } from '../../../../utils/update-details-hook'
import moment from 'moment'

interface IProps {
  key: string
  dataObj: any,
  outerSubmit: any,
  compKey: string,
  mapState: any
  classes: ClassNameMap
}

const MedReconcilateComponent: React.FC<IProps> = ({
  key,
  dataObj,
  outerSubmit,
  compKey,
  mapState,
  classes
}) => {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [openFhirJson, setOpenFhirJson] = useState(false)
  const [medTitle, setMedTitle] = useState('')
  const [medMoreDetails, setMedMoreDetails] = useState({} as any)
  const [selectedNotes, setSelectedNotes] = useState([] as number[])
  const [syncedMedIds, setSyncedMedIds] = useState<Set<number>>(new Set())
  // Session-scoped set of rows the provider has "gone over" during this reconciliation
  // sitting — either by an explicit action (confirm/save/sync) or by clicking the
  // lightweight "Reviewed" toggle for rows that need no change. Resets when the dialog closes.
  const [handledMedIds, setHandledMedIds] = useState<Set<number>>(new Set())

  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const uiResource = useSelector((state: RootState) => state.metaData.metadata)
  const patientService = new PatientService()
  const patientDetailStatus: any = useSelector((state: RootState) => state.patientDetail.status)
  const [data, setData] = useState<{ medList: any[], approvedMedList: any[] }>({ medList: [], approvedMedList: [] })
  const { currentRole } = useCurrentUser()
  const { updatePatientData } = useUpdateDataAction(patientDetail.id)

  useEffect(() => {
    if (patientDetailStatus !== 'loading' && patientDetail.id) {
      getMedReconcileList()
    }
  }, [patientDetail.id, patientDetailStatus])

  const getMedReconcileList = () => {
    patientService.getMedReconcilation(patientDetail.id, '').then((res: any) => {
      updateMedList(res?.data)
    }).catch((error: any) => {
      console.error('Error fetching medication reconciliation:', error)
      toast.error('Failed to load medication reconciliation data', Helper.bottom_center())
    })
  }

  const handleFhirJsonClose = () => setOpenFhirJson(false)

  // A row's stable identity / handled-set key: the existing med id when present,
  // else the imported med id (quarantine/import-only rows).
  const getRowKey = (row: any): number | undefined =>
    row?.medication?.id ?? row?.non_synced_medication?.id

  // Drug name used to alpha-sort rows — existing med first, fall back to imported.
  const getRowSortName = (row: any): string => {
    const m = Object.keys(row?.medication || {})?.length > 0 ? row?.medication : row?.non_synced_medication
    return (m?.pu_med_name || m?.name || '').toString().toLowerCase()
  }

  // True when the existing med has been set to "document discontinue". Match the
  // serialized display value AND the enum key, case-insensitively, to be robust.
  const isDocumentDiscontinued = (med: any): boolean => {
    const v = (med?.intake_regularity || '').toString().toLowerCase().replace(/_/g, ' ')
    return v === 'document discontinue'
  }

  const markHandled = (medId: any) => {
    if (medId === undefined || medId === null) return
    setHandledMedIds(prev => new Set(prev).add(+medId))
  }

  const toggleHandled = (medId: any) => {
    if (medId === undefined || medId === null) return
    setHandledMedIds(prev => {
      const next = new Set(prev)
      next.has(+medId) ? next.delete(+medId) : next.add(+medId)
      return next
    })
  }

  const updateMedList = (item: any[]) => {
    const stage = (item || [])
      // (3) Hide rows whose existing med was switched to "document discontinue".
      .filter((e: any) => !isDocumentDiscontinued(e?.medication))
      .map((e: any) => {
        return {
          ...e,
          medication: Object.keys(e?.medication || {})?.length > 0 ? { ...e?.medication, mode: 'view' } : e?.medication,
          non_synced_medication: Object.keys(e?.non_synced_medication || {})?.length > 0 ? { ...e?.non_synced_medication, mode: 'view' } : e?.non_synced_medication,
        }
      })
      // (1) Alphabetical by drug name (case-insensitive).
      .sort((a: any, b: any) => {
        const an = getRowSortName(a), bn = getRowSortName(b)
        return an > bn ? 1 : an < bn ? -1 : 0
      })
    setData({ medList: stage, approvedMedList: [] })
  }

  const handleMedClose = (_id: any) => updateMode('', 'view')

  const updateMode = (_id: any, mode: any) => {
    mode = _id !== '' ? mode : 'view'

    const currItem = data?.medList?.map((el: any) => {
      if (Object.keys(el?.medication || {})?.length > 0) {
        return el?.medication?.id === _id
          ? { ...el, medication: { ...el?.medication, mode: mode } }
          : el
      } else if (Object.keys(el?.non_synced_medication || {})?.length > 0) {
        return el?.non_synced_medication?.id === _id
          ? { ...el, non_synced_medication: { ...el?.non_synced_medication, mode: mode } }
          : el
      }
      return el
    }) || []

    setData({ ...data, medList: currItem })
  }

  const onSetDetail = (_id: number) => updateMode(_id, 'edit')

  const writeMedications = (med_id: any) => {
    const mrn = patientDetail?.mrns?.[0];
    if (!mrn) return;

    setLoading(true);
    patientService.syncMedToElationPatient(mrn, med_id).then((_response: any) => {
      setLoading(false)
      // Track this medication as synced (immediate feedback)
      setSyncedMedIds(prev => new Set(prev).add(med_id))
      // Syncing a med counts as having gone over that row.
      markHandled(med_id)
      // Refresh med list to get updated needs_elation_sync from server
      getMedReconcileList()
      updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
      setTimeout(() => {
        if (patientDetailStatus !== 'loading')
          toast.success('Medication written successfully to elation', Helper.bottom_center())
      }, 2500)
    }).catch((err: any) => {
      setLoading(false)
      toast.error(Helper.getErrorText(err), Helper.bottom_center())
    })

  }

  const setIcons = (isExistingMed: any, med_id: any, medData?: any) => {
    if (isExistingMed === true) {
      // Use needs_elation_sync from API response (false = synced, true = needs sync)
      // Fall back to local state for immediate feedback after sync
      const needsSync = medData?.needs_elation_sync ?? !syncedMedIds.has(med_id)
      return [
        { iconType: 'edit', callback: onSetDetail },
        { iconType: needsSync ? "sync" : "synced", callback: (med_id: any) => { writeMedications(med_id) } },
        { iconType: 'description', callback: onNotesClick },
        { iconType: 'more', callback: {} },
      ]
    }
    return []
  }

  const onNotesClick = (_id: any) => {
    if (selectedNotes.includes(+_id)) {
      setSelectedNotes([...selectedNotes.filter((f) => f !== +_id)])
    } else {
      setSelectedNotes([...selectedNotes, +_id])
    }
  }

  const outer = (_id: string, _mode: string) => {
    if (_mode === 'view') {
      updateMode(_id, 'view')
    }
  }

  const getIntakeReg = (reg: string) => {
    return Object.keys(uiResource?.INTAKE_REGULARITIES || {})?.find((key: any) => uiResource.INTAKE_REGULARITIES[key] === reg)
  }

  const confirmMedicine = (medicinedata: any) => {
    if (medicinedata?.id && patientDetail.id) {
      const payload = { entity_ref: 'medication', entity_id: medicinedata.id, confirmed_by: patientDetail.id }
      patientService.PostConfirmations(payload).then((_response: any) => {
        markHandled(medicinedata.id)
        updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
      }).catch((err: any) => {
        updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
        toast.error(Helper.getErrorText(err), Helper.bottom_center())
      })
      updateMode(medicinedata?.id, 'view')
    }
  }

  const handleSyncBack = () => {
    const mrn = patientDetail?.mrns?.[0];
    if (!mrn) return;

    setLoading(true);
    patientService.syncToElationPatient(mrn).then((_response: any) => {
      setLoading(false)
      updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
      setTimeout(() => {
        if (patientDetailStatus !== 'loading')
          toast.success('Patient data synced successfully to elation', Helper.bottom_center())
      }, 2500)
    }).catch((err: any) => {
      setLoading(false)
      toast.error(Helper.getErrorText(err), Helper.bottom_center())
    })
  }

  const postConfirmations = (
    values: any,
    _mode: any,
    _typeOut: any,
    medicinedata: any,
    dosageInfo: any,
    intake: any
  ) => {
    let payload: any
    setLoading(true)

    if (intake === 'not taking') {
      payload = {
        med_id: medicinedata.med_id || medicinedata.id,
        med_type: medicinedata.med_type,
        intake_regularity: getIntakeReg(intake),
        user_role: currentRole,
        notes: values.note,
        prescription_taken: values.prescription_taken
      }
    } else {
      payload = {
        med_id: medicinedata.med_id || medicinedata.id,
        med_type: medicinedata.med_type,
        num_units: values.num_units,
        delivery_method: values.delivery_method,
        intake_quantity: values.intake_quantity,
        intake_regularity: getIntakeReg(intake),
        frequency: values.frequency,
        dosage_info: dosageInfo,
        source_type: medicinedata.source_type,
        notes: values.note,
        user_role: currentRole,
        prescription_taken: values.prescription_taken,
        nn_sig_directions: values?.nn_sig_directions || ''
      }
      if (medicinedata?.care_plan_id) {
        payload = { ...payload, schedule_name: values?.schedule_name }
      }
    }

    patientService.putOtherMedication(patientDetail.id, medicinedata.id, payload).then((response: any) => {
      markHandled(medicinedata.id)
      if (_typeOut === 'confirm') {
        confirmMedicine(response?.data)
      } else if (_typeOut === 'save') {
        updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
      }
      if (response?.data?.message && intake === 'document discontinue') {
        updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
        toast.success(response?.data?.message, Helper.bottom_center())
      }
      // Refresh the med reconciliation list to show updated data
      getMedReconcileList()
    }).catch((error: any) => {
      toast.error(Helper.getErrorText(error), Helper.bottom_center())
    }).finally(() => {
      setLoading(false)
      if (intake !== 'document discontinue')
        updateMode(medicinedata?.id, 'view')
    })
  }

  const formatSubHeaderImportedMed = (d: any) => {
    const num_units = d?.pu_order?.num_units ? d.pu_order.num_units : ''
    const formulation = d?.pu_order?.formulation_display_name ? `${d.pu_order.formulation_display_name},` : ''
    const route = d?.pu_order?.route_display_name ? `${d.pu_order.route_display_name},` : ''
    const frequency = uiResource?.MEDICATION_DOSAGE_FREQUENCY?.[d?.pu_order?.frequency] ?
      uiResource.MEDICATION_DOSAGE_FREQUENCY[d.pu_order.frequency] : ''
    const text = `${num_units}${formulation}${route}${frequency}` ? `${num_units} ${formulation} ${route} ${frequency}` : 'Details not available'
    return <>{text}</>
  }

  const formatMedDetails = (pu_rec: any[], dosage_info: any[]) => {
    return checkCondition(Object.keys(pu_rec || {}).length > 0, getMedDetails(pu_rec, dosage_info), '')
  }

  const getMedDetails = (temp: any, dosage_info: any) => {
    const number = temp?.num_units || ''
    const formulation = temp?.formulation_display_name || ''
    const route = temp?.route_display_name || ''
    const frequency = uiResource?.MEDICATION_DOSAGE_FREQUENCY?.[temp?.frequency] || ''
    const dosageInfo = (dosage_info) && dosage_info?.[0]?.medicationTimings
      ? uiResource?.MEDICATION_TIMINGS?.[dosage_info[0]['medicationTimings']]
      : ''
    const text = `${number}${formulation}${route}${frequency}` ? `${number} ${formulation} ${route} ${Helper.toLowerlCase(frequency)} ${dosageInfo}` : 'Details not available'
    return `${text}`
  }

  const formatSubHeader = (d: any) => {
    if (Object.keys(d)?.length > 0) {
      const num_units = d?.pu_order?.num_units ? d.pu_order?.num_units : ''
      const formulation = d?.pu_order?.formulation_display_name ? `${d.pu_order?.formulation_display_name}` : ''
      const route = d?.pu_order?.route_display_name ? `${d.pu_order?.route_display_name}` : ''
      const frequency = uiResource.MEDICATION_DOSAGE_FREQUENCY[d.pu_order?.frequency] ?
        uiResource.MEDICATION_DOSAGE_FREQUENCY[d.pu_order?.frequency] : ''
      const dosageInfo = (d?.dosage_info) && d?.dosage_info?.[0]?.medicationTimings
        ? uiResource.MEDICATION_TIMINGS[d?.dosage_info[0]['medicationTimings']]
        : ''
      const text = `${num_units}${formulation}${route}${frequency}`
        ? `${num_units} ${formulation} ${route} ${frequency} ${dosageInfo}`
        : 'Details not available'

      const isSchedule = (d?.care_plan_id !== null && (d?.aa_rec) && d.needs_update)
        ? <Tooltip data-testid='schedule-need' title={'Schedule is not chosen'} placement="bottom">
          <IconButton size='small' className={classes.infoIcon}>
            <NoteAddIcon className={classes.schInfoIcon} />
          </IconButton>
        </Tooltip>
        : ''

      const isDisAllowSub = d.disallow_substitution
        ? <Tooltip data-testid='disallow_substitution' title={'Disallow substitution true'} placement="bottom">
          <IconButton size='small' className={classes.infoIcon}>
            <BlockIcon className={classes.infoIconFont} sx={{ fill: '#d94040de' }} />
          </IconButton>
        </Tooltip>
        : ''
      return <>{text}{' '}<IngredientTooltip ingredients={d?.calc_dose} />{isSchedule}{isDisAllowSub}</>
    }
    else return ''
  }

  const formatSubHeader1 = (d: any) => {
    if (Object.keys(d?.pu_rec || {})?.length > 0) {
      if (d?.titration_status !== 'initiate_current') {
        const medDetails = checkCondition(d?.intake_started_on === null && d?.pu_rec !== undefined, formatMedDetails(d.pu_rec, d?.dosage_info), '')
        const text = checkCondition(d?.intake_started_on === null && d?.pu_rec !== undefined, `Start ${medDetails}`, '')
        return <>{text}</>
      }
    }
    return 'Details not available'
  }

  const cardComponent = (d: any, changedColList: any, isExistingMed = false) => {
    const modifiedDate = d?.modified_on
      ? moment.unix(d.modified_on).format('MM/DD/YYYY')
      : ''

    if (d?.is_recommended === true) {
      return <>
        <CardLayoutComponent
          id={d.id}
          name={d?.name}
          leftHeader={checkCondition(d?.titration_status === 'initiate', Helper.getRXnorm(d?.pu_med_name, d?.rxnorm), Helper.getRXnorm(d?.pu_order?.name, d?.taking_med_rxnorm))}
          subHeader={formatSubHeader(d)}
          leftHeader1={checkCondition((d?.titration_status)?.includes('discontinue') || (d?.titration_status)?.includes('initiate_current'), '', checkCondition(d?.intake_started_on === null && d?.titration_status !== 'initiate' && d?.pu_order?.name !== d?.pu_med_name, `${checkCondition(d?.pu_med_name !== null, Helper.getRXnorm(d?.pu_med_name, d?.rxnorm), Helper.getRXnorm(d?.name, d?.rxnorm))} `, ``))}
          subHeader1={checkCondition((d?.titration_status)?.includes('discontinue') || (d?.titration_status)?.includes('initiate_current'), '', checkCondition(d?.titration_status !== 'initiate' && d?.intake_started_on === null, formatSubHeader1(d), ''))}
          rightHeader={''}
          bottomText={modifiedDate}
          source={`${d?.source_type} - ${d?.source_system ? d?.source_system : 'ATMAN'}`}
          needs_confirmation={
            d?.source_type === 'imported'
              ? d?.needs_confirmation || undefined
              : undefined
          }
          needs_update={d?.needs_update || undefined}
          icons={[]}
          is_recommended={d?.is_recommended}
          programs={d?.programs}
          onRefClick={() => undefined}
          onMedDetailsClick={() => getMedMoreDetails(patientDetail.id, d.id, d.pu_med_name ? d.pu_med_name : d.name, d?.rxnorm)}
          is_referred={d.is_referred}
          is_overridden={false}
          entityRef="medication"
          patientId={patientDetail?.id}
          is_care_plan_non_recc={
            !(d?.is_recommended || d?.is_referred)
          }
          category={'medications'}
          titration_status={d?.titration_status}
          recommendation_source={d?.recommendation_source}
        />
        {selectedNotes.includes(d.id) && isExistingMed && (
          <NotesComponent
            entityId={d.id}
            entityRef={'medication'}
            cancelBtn={true}
            onCancel={onNotesClick}
          />
        )}
      </>
    } else {
      return <><CardLayoutComponent
        id={d?.id}
        leftHeader={Helper.getRXnorm(d?.pu_med_name ? d?.pu_med_name : d?.name, d?.rxnorm)}
        rightHeader={d?.intake_regularity?.charAt(0).toUpperCase() + d?.intake_regularity?.slice(1) || ''}
        subHeader={formatSubHeaderImportedMed(d)}
        bottomText={''}
        needs_confirmation={d?.needs_confirmation || undefined}
        needs_update={d?.needs_update || undefined}
        icons={setIcons(isExistingMed, d?.id || d?.med_id, d)}
        programs={d?.programs || undefined}
        onRefClick={() => undefined}
        is_referred={false}
        is_overridden={false}
        is_new_med={d?.is_new_med || undefined}
        onOverrideClick={undefined}
        onMedDetailsClick={() => getMedMoreDetails(patientDetail.id, d.id, d.pu_med_name ? d.pu_med_name : d.name, d?.rxnorm)}
        entityRef={isExistingMed
          ? 'med_reconcilation'
          : d?.med_id !== null && d?.is_quarantine === false
            ? 'med_reconcilation_mapped'
            : 'med_reconcilation_quarantine'
        }
        patientId={patientDetail?.id}
        category={'medications'}
      />
        {selectedNotes.includes(d.id) && isExistingMed && (
          <NotesComponent
            entityId={d.id}
            entityRef={'medication'}
            cancelBtn={true}
            onCancel={onNotesClick}
          />
        )
        }</>
    }
  }

  const renderMedCard = (d: any, changedColList: any, isExistingMed = false) => {
    // Early return if d is null/undefined
    if (!d)
      return null

    return <>
      {cardComponent(d, changedColList, isExistingMed)}

      {(d.mode === 'edit') && (
        <Box mb={1} className={classes.fullWidth}>
          <Card variant='outlined'
            className={
              d?.needs_confirmation
                ? [
                  classes.confirmCardBackground,
                  classes.questionCardContent,
                  classes.fullWidth
                ].join(' ')
                : classes.questionCardContent
            }
          >
            <CardContent className={classes.zeroPadding}>
              <Box p={1} pb={0.8} pt={0.3}>
                <Typography variant="subtitle2">
                  <small> {d.pu_med_name ? d.pu_med_name : d.name}</small>
                </Typography>
                <Typography color="textSecondary" className={classes.desc} variant="body2">{formatSubHeader(d)}</Typography>
              </Box>

              <MedicationsComponent
                action='edit'
                medicineInfo={{ ...d, is_recommended: patientDetail?.is_recommended || false }}
                writeMode={(d?.intake_regularity === 'not taking') ? false : true}
                noDropDown="none"
                heading=""
                handleClose={handleMedClose}
                patientId={patientDetail?.id}
                currentStage={''}
                differently={true}
                classes={classes}
                input={{ ...d, isMedReconcile: true }}
                outer={outer}
                outerSubmit={postConfirmations}
                uiResource={uiResource}
              />
            </CardContent>
          </Card >
        </Box >
      )}
      {
        changedColList?.length > 0 &&
        <Box className={classes.cardsContentPadding}>
          <Grid container spacing={1} direction="column">
            <Grid item>
              <Typography variant="body2" className={classes.subTitle}>Changes</Typography>
            </Grid>
            <Box pb={1}>
              <CardContent className={classes.confirmCardContent}>
                <Grid container spacing={1} direction='column'>
                  {changedColList?.map((currItem: any, index: number) => {
                    return <Grid item key={index}>
                      <Typography variant="h5" className={classes.desc}>{currItem}:
                        {typeof d[currItem] === 'string'
                          ? <Typography component={'span'} variant="h5" className={classes.desc}> {d[currItem]} </Typography>
                          :
                          <Box className={classes.jsonViewBox}>
                            <JsonView
                              data={d[currItem]}
                              shouldExpandNode={() => false}
                              style={defaultStyles}
                            />
                          </Box>
                        }
                      </Typography>
                    </Grid>
                  })}
                </Grid>
              </CardContent>
            </Box>
          </Grid>
        </Box>
      }
    </>
  }

  const handleClose = () => {
    setOpen(false)
  }
  const handleSubmit = (response: any) => {
    //  response && setData([...data, response]) 
  }

  const getMedMoreDetails = (pId: string, recId: string, medName: string, code: any) => {
    setLoading(true)
    setMedTitle(`${medName} ${code ? `[${code}]` : ''}`)

    patientService.getMedMoreDetails(pId, recId)
      .then((response: any) => {
        setMedMoreDetails(response.data?.fhir_json);
        setLoading(false)
      }).catch((err: any) => {
        setLoading(false)
        console.error(err);
      });
    setOpenFhirJson(true)
  }

  return (<Box>
    {loading &&
      <Box sx={{ margin: '-1rem -0.9rem 1rem -0.9rem' }}> <LinearProgress /></Box>
    }
    <Box alignItems={'flex-end'} justifyContent={'flex-end'} display={'flex'}>
      <Button variant="outlined" sx={{ marginRight: '1.4rem' }}
        disabled={open}
        onClick={() => setOpen(true)}
        className={`${classes.taskAddBtn} ${classes.medSubSelectActiveBtn}`}
      >
        <Typography className={classes.desc} variant="body2"> {'Add new med'}</Typography>
      </Button>
    </Box>

    {open && (
      <Box width={'65%'} sx={{ padding: '0.5rem 1rem 1rem 1.5rem' }}>
        <AddNewMedicationCard
          classes={classes}
          patientId={patientDetail.id}
          isMedReconcile={true}
          currentStage={''} //currentProgram ? getStageString(currentProgram) : ''}
          uiResource={uiResource}
          handleClose={handleClose}
          handleSubmit={handleSubmit}
        />
      </Box>
    )}

    <Grid container spacing={0} direction="column" className={classes.contextualHelpContainer}>
      <Grid item className={classes.fullWidth}>
        {data && Object.keys(data)?.length > 0 &&
          <TableContainer component={Paper} style={{ maxHeight: '35rem' }}>
            <Table stickyHeader size="small" aria-label="med-reconcilate-table">
              <TableHead>
                <TableRow className={classes.medHistoryTitles}>
                  <TableCell className={[classes.tableReconcileHeader, classes.reconcileHeader].join(' ')} width='44%' align='center'>
                    <Typography variant='subtitle2' className={classes.subHeaderTitle}>Imported medications</Typography>
                  </TableCell>
                  <TableCell className={[classes.tableReconcileHeader, classes.reconcileHeader].join(' ')} width={'44%'} align='center'>
                    <Typography variant='subtitle2' className={classes.subHeaderTitle}>Existing medications</Typography>
                  </TableCell>
                  <TableCell className={[classes.tableReconcileHeader, classes.reconcileHeader].join(' ')} width={'8%'} align='center'>
                    <Typography variant='subtitle2' className={classes.subHeaderTitle}>Reviewed</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.medList)?.map((item: any, index: number) => {
                  const rowKey = getRowKey(item)
                  const isHandled = rowKey !== undefined && rowKey !== null && handledMedIds.has(+rowKey)
                  return <TableRow key={`imported-${rowKey ?? index}`}>
                    <TableCell className={classes.tableReconcileHeader}>
                      <Box sx={{ opacity: isHandled ? 0.55 : 1 }}>
                        {renderMedCard(item?.non_synced_medication, item?.changed_columns_list, false)}
                      </Box>
                    </TableCell>
                    <TableCell className={classes.tableReconcileHeader}>
                      <Box sx={{ opacity: isHandled ? 0.55 : 1 }}>
                        {renderMedCard(item?.medication, item?.changed_columns_list, true)}
                      </Box>
                    </TableCell>
                    <TableCell className={classes.tableReconcileHeader} align='center' sx={{ verticalAlign: 'middle' }}>
                      <Tooltip
                        title={isHandled ? 'Reviewed — click to undo' : 'Mark as reviewed'}
                        placement='left'
                      >
                        <span>
                          <IconButton
                            size='small'
                            disabled={rowKey === undefined || rowKey === null}
                            onClick={() => toggleHandled(rowKey)}
                          >
                            {isHandled
                              ? <CheckCircleIcon sx={{ fontSize: '1.1rem', color: '#4caf50' }} />
                              : <CheckCircleOutlineIcon sx={{ fontSize: '1.1rem', color: '#9e9e9e' }} />
                            }
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                })}
              </TableBody>
            </Table>
          </TableContainer>
        }
      </Grid>
    </Grid>

    {openFhirJson &&
      <MedDetailsComponent
        openFhirJson={openFhirJson}
        medTitle={medTitle}
        handleFhirJsonClose={handleFhirJsonClose}
        medMoreDetails={medMoreDetails} />
    }
  </Box>
  )
}

export default withStyles(stylesheet)(MedReconcilateComponent)